/**
 * useDoctrine.js — single hook that pulls the server-side rules engines
 * (resilient pipeline directive, asset-holder taxonomy, firm identity)
 * once per session and caches them.  Any frontend surface that renders
 * doctrine-driven language, status strings, or branding reads through
 * this hook instead of hardcoding literals.
 *
 * The backend is the single source of truth (per Shannon's directive).
 * If either endpoint fails we return {} so the caller can fall back to
 * benign defaults — the field flow never blocks on doctrine fetch.
 */
import { useEffect, useState } from "react";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const CACHE_KEY = "mf_doctrine_cache_v1";
const CACHE_TTL_MS = 15 * 60 * 1000; // 15 min — doctrine changes are rare

let _inflight = null;

async function loadDoctrine() {
  try {
    const cached = sessionStorage.getItem(CACHE_KEY);
    if (cached) {
      const parsed = JSON.parse(cached);
      if (parsed && parsed.ts && Date.now() - parsed.ts < CACHE_TTL_MS) {
        return parsed.data;
      }
    }
  } catch (_) { /* ignore parse errors */ }

  if (_inflight) return _inflight;
  _inflight = (async () => {
    try {
      const [directiveRes, firmRes] = await Promise.all([
        fetch(`${BACKEND_URL}/api/resilient-pipeline/directive`).then(r => r.json()),
        fetch(`${BACKEND_URL}/api/asset-holder/firm-identity`).then(r => r.json()),
      ]);
      const data = {
        directive: directiveRes || {},
        firm: firmRes || {},
        loaded_at: new Date().toISOString(),
      };
      try {
        sessionStorage.setItem(CACHE_KEY, JSON.stringify({ ts: Date.now(), data }));
      } catch (_) { /* quota exceeded — non-fatal */ }
      return data;
    } catch (_) {
      return { directive: {}, firm: {}, loaded_at: null };
    } finally {
      _inflight = null;
    }
  })();
  return _inflight;
}

export function useDoctrine() {
  const [state, setState] = useState({ directive: {}, firm: {}, loaded: false });
  useEffect(() => {
    let alive = true;
    loadDoctrine().then((data) => {
      if (alive) setState({ ...data, loaded: true });
    });
    return () => { alive = false; };
  }, []);
  return state;
}

export function useUiStatusStrings() {
  const { directive } = useDoctrine();
  return directive?.ui_status_strings || {};
}
